import React, { useState } from 'react';
import PropTypes from 'prop-types';

import PopOverMenu from '../../../PopOverMenu';
import useIsMobile from '../../../hooks/useIsMobile';
import {
    NOTE_MENU_VALUE,
    NOTE_MENU_LABEL,
    NOTE_MENU_TESTID,
    REMOVE_MENU_VALUE,
    REMOVE_MENU_LABEL,
    REMOVE_MENU_TESTID,
    ACTIONS_SHEET_TITLE,
    ACTIONS_NOTE_LABEL,
    ACTIONS_REMOVE_LABEL,
    ACTIONS_CANCEL_LABEL,
} from './constants';
import styles from './playersList.scss';

// Kebab shell shared by the desktop row and the mobile card. Add/Edit Player Note (TMS-2180) sits above
// Remove Player from List (TMS-2179), matching Figma 185-11717. The note item pins its own `.menu.note`
// testid; remove pins `.menu.item-remove` — the same suffixes back both presentations below.
const MENU_ITEMS = [
    { value: NOTE_MENU_VALUE, label: NOTE_MENU_LABEL, testId: NOTE_MENU_TESTID },
    { value: REMOVE_MENU_VALUE, label: REMOVE_MENU_LABEL, testId: REMOVE_MENU_TESTID },
];

const RowKebab = ({ onNote, onRemove, 'data-testid': dataTestId }) => {
    const { isMobileWidth } = useIsMobile();
    const [isSheetOpen, setIsSheetOpen] = useState(false);

    const runAction = (value) => {
        if (value === NOTE_MENU_VALUE) {
            onNote();
        } else if (value === REMOVE_MENU_VALUE) {
            onRemove();
        }
    };

    const handleSelected = item => runAction(item.value);

    const closeSheet = () => setIsSheetOpen(false);

    const handleSheetAction = (value) => {
        closeSheet();
        runAction(value);
    };

    // Mobile renders the actions as a centered iOS-style action sheet (371-12193) rather than the
    // absolutely-positioned dropdown, which the card layout clips. Its copy reads as full sentences and
    // the destructive Remove is red. Desktop keeps the PopOverMenu dropdown so its established testids
    // and copy are untouched.
    if (isMobileWidth) {
        return (
            <div className={styles.menuCol}>
                <button
                    type="button"
                    className={styles.menuAnchorButton}
                    onClick={() => setIsSheetOpen(true)}
                    data-testid={dataTestId && `${dataTestId}.anchor`}
                >
                    <span className="far fa-ellipsis-v" />
                </button>
                {isSheetOpen && (
                    <div
                        className={styles.actionSheetOverlay}
                        onClick={closeSheet}
                        data-testid={dataTestId || undefined}
                    >
                        <div
                            className={styles.actionSheet}
                            onClick={event => event.stopPropagation()}
                            data-testid={dataTestId && `${dataTestId}.actions-sheet`}
                        >
                            <div className={styles.actionSheetTitle} data-testid={dataTestId && `${dataTestId}.title`}>
                                {ACTIONS_SHEET_TITLE}
                            </div>
                            <button
                                type="button"
                                className={styles.actionPill}
                                onClick={() => handleSheetAction(NOTE_MENU_VALUE)}
                                data-testid={dataTestId && `${dataTestId}.${NOTE_MENU_TESTID}`}
                            >
                                {ACTIONS_NOTE_LABEL}
                            </button>
                            <button
                                type="button"
                                className={`${styles.actionPill} ${styles.actionPillDanger}`}
                                onClick={() => handleSheetAction(REMOVE_MENU_VALUE)}
                                data-testid={dataTestId && `${dataTestId}.${REMOVE_MENU_TESTID}`}
                            >
                                {ACTIONS_REMOVE_LABEL}
                            </button>
                            <button
                                type="button"
                                className={`${styles.actionPill} ${styles.actionPillCancel}`}
                                onClick={closeSheet}
                                data-testid={dataTestId && `${dataTestId}.cancel`}
                            >
                                {ACTIONS_CANCEL_LABEL}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={styles.menuCol}>
            <PopOverMenu
                menuItems={MENU_ITEMS}
                onSelected={handleSelected}
                anchorClass={styles.menuAnchor}
                data-testid={dataTestId || undefined}
            />
        </div>
    );
};

RowKebab.propTypes = {
    onNote: PropTypes.func,
    onRemove: PropTypes.func,
    'data-testid': PropTypes.string,
};

RowKebab.defaultProps = {
    onNote: () => {},
    onRemove: () => {},
    'data-testid': null,
};

export default RowKebab;
